import { LocalDateTime } from 'src/utilities/local-date-time.js';
import type { RiskAssessmentViewModel, SubstanceViewModel } from '@trinnvis/dabih-api-client';
import type { OrganizationState, State } from 'src/store/types.js';
import type { SubstancesPageViewModel } from 'src/pages/substances-page/substances-page-view-model.js';
import { getOrganization, getOrganizationId, key, riskAssessmentsNotDeleted } from 'src/store/selectors';
import type { PageLinks } from 'src/pages/overview-page/overview-page-view-model';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import { substancesNotDeleted } from '../../store/selectors/assets/substances-not-deleted';
import { getOrganizationId as getOrganizationId1 } from '../../store/selectors/organization/getOrganizationId';
import type { TableItemsSubstance } from '../../pages/substances-page/table-items-substance';
import { BASE_PATH } from 'src/store';
import { getPage } from '../../store/selectors/pages/getPage';
import { pageIds } from '../../store/selectors/pages/pageIds';
import { getPages } from '../../store/selectors/pages/getPages';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { t } from 'src/i18n';

export function substancesPageView(
  hrefPrefix: string,
  viewModel: AbstractPageView,
  state: State,
): SubstancesPageViewModel {
  const organization = getOrganization(state);
  if (organization === undefined) {
    throw new Error('Illegal state (E154), organization not found');
  }
  const featureStates = getFeatureStates(state);
  const riskAssessments = riskAssessmentsNotDeleted(state);
  const linkedPage = getPage(pageIds.hse, getPages(state));
  const pageLinks: PageLinks = {
    type: 'page-links',
    items: [],
  };
  if (featureStates.core || featureStates.hse) {
    pageLinks.items.push({
      name: t('substances.goToThemePage', { name: linkedPage?.name }) || '',
      type: '',
      href: '/account/' + getOrganizationId(state) + '/' + pageIds.hse,
    });
  }

  const organizationId = getOrganizationId1(state);
  return {
    ...viewModel,
    type: 'substances-page',
    href: hrefPrefix,
    icon: 'substances',
    substances: substancesNotDeleted(state)
      .filter((s) => s.archived === false)
      .map((s) => {
        return toTableItemSubstance(hrefPrefix, s, organization, riskAssessments);
      }),
    substancesArchived: substancesNotDeleted(state)
      .filter((s) => s.archived === true)
      .map((s) => {
        return toTableItemSubstance(hrefPrefix, s, organization, riskAssessments);
      }),
    key: key(state),
    pdfHref: BASE_PATH + '/organizations/' + organizationId + '/substances/substances-list.pdf?key=' + key(state),
    pageLinks,
  };
}

function toTableItemSubstance(
  hrefPrefix: string,
  substance: SubstanceViewModel,
  organization: OrganizationState,
  riskAssessments: RiskAssessmentViewModel[],
): TableItemsSubstance {
  const toState = () => {
    if (substance.uploadStatus === 'PENDING' || substance.uploadStatus === 'PRELIMINARY') {
      return 'pending';
    } else if (substance.uploadStatus === 'UNREADABLE') {
      return 'unreadable';
    } else return '';
  };

  const toSupplier = () => {
    if (substance.supplierUuid) {
      return organization.partnersById[substance.supplierUuid].name;
    } else {
      return '';
    }
  };

  const toRisk = () => {
    const risks = riskAssessments
      .filter((item) => item.relatedSubstance === substance.uuid)
      .map((item) => (item.probability ?? 0) * (item.consequence ?? 0));
    if (risks.length === 0) return '';
    const highestRisk = Math.max(...risks);
    if (highestRisk) {
      if (highestRisk > 0 && highestRisk < 5) {
        return '1';
      }
      if (highestRisk > 4 && highestRisk < 9) {
        return '2';
      }
      if (highestRisk > 8 && highestRisk < 13) {
        return '3';
      }
      if (highestRisk > 12) {
        return '4';
      }
    }
    return '';
  };

  const result: TableItemsSubstance = {
    state: toState(),
    uploaded: toUploadedDisplay(substance.uploadDateTime),
    uploadedSortValue: substance.uploadDateTime ?? '',
    name: substance.name ?? '',
    supplier: toSupplier(),
    hazardLabels: substance.hazardLabels ?? [],
    applications: substance.applications ?? '',
    location: substance.location ?? '',
    risk: toRisk(),
    href: hrefPrefix + 'substances/' + substance.uuid,
    uuid: substance.uuid,
    hasDraft: substance.hasDraft,
  };
  return result;
}

function toUploadedDisplay(dateTime?: string): string {
  if (dateTime === undefined || dateTime === '') {
    return '';
  }
  return LocalDateTime.fromIsoString(dateTime).toStringForDisplay();
}
